import { BenefitsBenefitAvailabilityFragment } from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'

export type BenefitAvailabilityStatus = 'active' | 'available' | 'pending' | 'not_active'
export type BenefitSetupStatus =
  | 'pending_setup'
  | 'pending_enrollment'
  | 'pending_activation'
  | 'active'

type SpanishBenefitId =
  | 'meals_es'
  | 'transport_es'
  | 'childcare_es'
  | 'health_insurance_es'
  | 'trainings_es'
  | 'wellness_es'
  | 'perks_es'

type PortugueseBenefitId = 'meals_pt' | 'health_insurance_pt' | 'wellness_pt'
type GermanBenefitId = '50_in_kind_de' | '60_special_events_de' | 'wellness_de'
export type SupportedBenefitId = SpanishBenefitId | PortugueseBenefitId | GermanBenefitId

export interface BenefitListing {
  id: SupportedBenefitId
  title: string
  description: string
  reviewDescription?: string
  availability: BenefitAvailabilityStatus | undefined
  emoji: string
  learnMoreUrl?: string
}

function isTrainingAvailable(featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]) {
  const trainingBenefitAvailable = featureAvailabilities?.find(
    (feature) => feature.id === 'benefits_trainings'
  )?.available
  const trainingProductAvailable = featureAvailabilities?.find(
    (feature) => feature.id === 'trainings_core'
  )?.available
  return trainingBenefitAvailable && trainingProductAvailable
}

function isPerksAvailable(featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]) {
  return (
    featureAvailabilities?.find((feature) => feature.id === 'benefits_perks')?.available ?? false
  )
}

const spainBenefits: (
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
) => BenefitListing[] = (featureAvailabilities) => {
  const trainingsEnabled = isTrainingAvailable(featureAvailabilities)

  const wellnessEnabled = featureAvailabilities?.find(
    (feature) => feature.id === 'benefits_wellness'
  )?.available

  const healthcareEnabled = featureAvailabilities?.find(
    (feature) => feature.id === 'benefits_healthcare'
  )?.available

  const perkEnabled = isPerksAvailable(featureAvailabilities)

  return [
    {
      id: 'meals_es',
      title: i18n.t('benefits.homepage.static_benefits.es.meals.title'),
      description: i18n.t('benefits.homepage.static_benefits.es.meals.description'),
      reviewDescription: i18n.t(
        'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.meals.description'
      ),
      emoji: '🍔',
      availability: 'active',
      learnMoreUrl:
        'https://help.factorialhr.com/beneficios/about-flexible-compensation-for-restaurant-tickets',
    },
    {
      id: 'transport_es',
      title: i18n.t('benefits.homepage.static_benefits.es.transport.title'),
      description: i18n.t('benefits.homepage.static_benefits.transport.description'),
      reviewDescription: i18n.t(
        'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.transport.description'
      ),
      emoji: '🚊',
      availability: 'active',
      learnMoreUrl:
        'https://help.factorialhr.com/beneficios/about-flexible-compensation-for-restaurant-tickets',
    },
    {
      id: 'childcare_es',
      title: i18n.t('benefits.homepage.static_benefits.es.nursery.title'),
      description: i18n.t('benefits.homepage.static_benefits.es.nursery.description'),
      reviewDescription: i18n.t(
        'benefits.tabs.employees.activation_results.modal.review_benefits.benefits_list.childcare.description'
      ),
      emoji: '👶',
      availability: 'active',
      learnMoreUrl:
        'https://help.factorialhr.com/benefits/about-flexible-remuneration-nursery-tickets',
      primaryAction: i18n.t('benefits.homepage.benefit.request'),
    },
    {
      id: 'health_insurance_es',
      title: i18n.t('benefits.homepage.static_benefits.es.health_insurance.title'),
      emoji: '🏥',
      description: i18n.t('benefits.homepage.static_benefits.es.health_insurance.description_v2'),
      availability: featureAvailabilities
        ? healthcareEnabled
          ? 'active'
          : 'not_active'
        : undefined,
      learnMoreUrl:
        'https://help.factorialhr.com/benefits/about-flexible-remuneration-health-insurance',
    },
    {
      id: 'trainings_es',
      title: i18n.t('benefits.homepage.static_benefits.es.trainings.title'),
      emoji: '📚',
      description: i18n.t('benefits.homepage.static_benefits.es.trainings.description'),
      availability: featureAvailabilities
        ? trainingsEnabled
          ? 'active'
          : 'not_active'
        : undefined,
    },
    {
      id: 'wellness_es',
      title: i18n.t('benefits.homepage.static_benefits.es.wellness.title'),
      emoji: '👟',
      description: i18n.t('benefits.homepage.static_benefits.es.wellness.description'),
      availability: featureAvailabilities ? (wellnessEnabled ? 'active' : 'not_active') : undefined,
    },
    ...(perkEnabled
      ? [
          {
            id: 'perks_es' as const,
            title: i18n.t('benefits.my_benefits.perk.title'),
            emoji: '🎯',
            description: i18n.t('benefits.my_benefits.perk.description'),
            availability: 'active' as const,
          },
        ]
      : []),
  ]
}

const portugalBenefits: (
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
) => BenefitListing[] = (featureAvailabilities) => {
  const healthcareEnabled = featureAvailabilities?.find(
    (feature) => feature.id === 'benefits_healthcare'
  )?.available

  return [
    {
      id: 'meals_pt',
      title: i18n.t('benefits.homepage.static_benefits.pt.meals.title'),
      emoji: '🍔',
      description: i18n.t('benefits.homepage.static_benefits.pt.meals.description'),
      availability: 'active',
    },
    {
      id: 'health_insurance_pt',
      title: i18n.t('benefits.homepage.static_benefits.pt.health_insurance.title'),
      emoji: '🏥',
      description: i18n.t('benefits.homepage.static_benefits.pt.health_insurance.description'),
      availability: featureAvailabilities
        ? healthcareEnabled
          ? 'pending'
          : 'not_active'
        : undefined,
    },
    {
      id: 'wellness_pt',
      title: i18n.t('benefits.homepage.static_benefits.pt.wellness.title'),
      emoji: '👟',
      description: i18n.t('benefits.homepage.static_benefits.pt.wellness.description'),
      availability: 'pending',
    },
  ]
}

const germanyBenefits: (
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
) => BenefitListing[] = () => {
  return [
    {
      id: '50_in_kind_de',
      title: i18n.t('benefits.homepage.static_benefits.de.50_in_kind.title'),
      emoji: '💰',
      description: i18n.t('benefits.homepage.static_benefits.de.50_in_kind.description'),
      availability: 'active',
    },
    {
      id: '60_special_events_de',
      title: i18n.t('benefits.homepage.static_benefits.de.60_special_events.title'),
      emoji: '💸',
      description: i18n.t('benefits.homepage.static_benefits.de.60_special_events.description'),
      availability: 'pending',
    },
    {
      id: 'wellness_de',
      title: i18n.t('benefits.homepage.static_benefits.de.wellness.title'),
      emoji: '👟',
      description: i18n.t('benefits.homepage.static_benefits.de.wellness.description'),
      availability: 'pending',
    },
  ]
}

export const getBenefitsList = (
  country: string,
  featureAvailabilities?: BenefitsBenefitAvailabilityFragment[]
): BenefitListing[] => {
  switch (country) {
    case 'pt':
      return portugalBenefits(featureAvailabilities)
    case 'de':
      return germanyBenefits(featureAvailabilities)
    default:
      return spainBenefits(featureAvailabilities)
  }
}
